import { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, Input, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import LiftlogicResourcePage from '../v2/LiftlogicResourcePage';
import useIncidentApi from 'hooks/useIncidentApi';
import IncidentDetailDrawer from './IncidentDetailDrawer';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Abierto', color: 'blue' },
  { value: 'in_progress', label: 'En progreso', color: 'gold' },
  { value: 'resolved', label: 'Resuelto', color: 'green' },
  { value: 'discarded', label: 'Descartado', color: 'default' },
];

const CATEGORY_OPTIONS = [
  { value: 'bug', label: 'Bug', color: 'red' },
  { value: 'suggestion', label: 'Sugerencia', color: 'green' },
  { value: 'question', label: 'Pregunta', color: 'blue' },
  { value: 'other', label: 'Otro', color: 'default' },
];

const EMAIL_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendiente', color: 'default' },
  { value: 'sent', label: 'Enviado', color: 'green' },
  { value: 'failed', label: 'Falló', color: 'red' },
];

const toLookup = (opts) => Object.fromEntries(opts.map((o) => [o.value, o]));
const STATUS_LOOKUP = toLookup(STATUS_OPTIONS);
const CATEGORY_LOOKUP = toLookup(CATEGORY_OPTIONS);
const EMAIL_STATUS_LOOKUP = toLookup(EMAIL_STATUS_OPTIONS);

const IncidentsPage = () => {
  const { message } = App.useApp();
  const api = useIncidentApi();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState(['open', 'in_progress']);
  const [category, setCategory] = useState([]);
  const [emailStatus, setEmailStatus] = useState([]);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.list({
        status,
        category,
        emailStatus,
        search: search || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setRows(data.rows ?? []);
      setTotal(data.total ?? 0);
    } catch (err) {
      message.error('No se pudieron cargar las incidencias.');
    } finally {
      setLoading(false);
    }
  }, [api, status, category, emailStatus, search, page, pageSize, message]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        title: '#',
        dataIndex: 'id',
        width: 64,
        render: (id) => <Typography.Text strong>#{id}</Typography.Text>,
      },
      {
        title: 'Categoría',
        dataIndex: 'category',
        width: 120,
        render: (c) => {
          const cfg = CATEGORY_LOOKUP[c];
          return <Tag color={cfg?.color ?? 'default'}>{cfg?.label ?? c}</Tag>;
        },
      },
      {
        title: 'Asunto',
        dataIndex: 'subject',
        ellipsis: true,
        render: (text) => (
          <Tooltip title={text} mouseEnterDelay={0.4}>
            <Typography.Text>{text}</Typography.Text>
          </Tooltip>
        ),
      },
      {
        title: 'Tenant',
        dataIndex: 'tenantId',
        width: 90,
        render: (t) => t ?? '—',
      },
      {
        title: 'Usuario',
        dataIndex: 'userId',
        width: 90,
        render: (u) => u ?? '—',
      },
      {
        title: 'Estado',
        dataIndex: 'status',
        width: 130,
        render: (s) => {
          const cfg = STATUS_LOOKUP[s];
          return <Tag color={cfg?.color ?? 'default'}>{cfg?.label ?? s}</Tag>;
        },
      },
      {
        title: 'Email',
        dataIndex: 'emailStatus',
        width: 110,
        render: (s) => {
          const cfg = EMAIL_STATUS_LOOKUP[s];
          return <Tag color={cfg?.color ?? 'default'}>{cfg?.label ?? s}</Tag>;
        },
      },
      {
        title: 'Creado',
        dataIndex: 'createdAt',
        width: 140,
        render: (d) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—'),
      },
    ],
    []
  );

  const toolbar = (
    <Space wrap>
      <Select
        mode="multiple"
        placeholder="Estado"
        style={{ minWidth: 220 }}
        value={status}
        onChange={(v) => {
          setPage(1);
          setStatus(v);
        }}
        options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        allowClear
      />
      <Select
        mode="multiple"
        placeholder="Categoría"
        style={{ minWidth: 180 }}
        value={category}
        onChange={(v) => {
          setPage(1);
          setCategory(v);
        }}
        options={CATEGORY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        allowClear
      />
      <Select
        mode="multiple"
        placeholder="Email"
        style={{ minWidth: 160 }}
        value={emailStatus}
        onChange={(v) => {
          setPage(1);
          setEmailStatus(v);
        }}
        options={EMAIL_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        allowClear
      />
      <Input
        placeholder="Buscar en asunto / descripción"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        onPressEnter={() => {
          setPage(1);
          setSearch(searchInput.trim());
        }}
        prefix={<SearchOutlined />}
        allowClear
        style={{ width: 280 }}
      />
      <Button
        icon={<ReloadOutlined />}
        onClick={() => {
          setSearch(searchInput.trim());
          load();
        }}
      >
        Actualizar
      </Button>
    </Space>
  );

  return (
    <LiftlogicResourcePage title="Incidencias reportadas" toolbar={toolbar}>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        onRow={(record) => ({
          onClick: () => setSelected(record.id),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50, 100],
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
          showTotal: (t) => `${t} incidencia${t === 1 ? '' : 's'}`,
        }}
      />
      <IncidentDetailDrawer
        open={selected !== null}
        incidentId={selected}
        onClose={() => setSelected(null)}
        onUpdated={load}
      />
    </LiftlogicResourcePage>
  );
};

export default IncidentsPage;
